import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocation, useSearchParams } from "react-router-dom";

import { EventsLibraryCreateButton } from "@/components/ui/admin/events/EventsLibraryCreateButton";
import { EventsLibraryKpis as EventsLibraryKpisPanel } from "@/components/ui/admin/events/EventsLibraryKpis";
import { EventsLibraryPanel } from "@/components/ui/admin/events/EventsLibraryPanel";
import { createEventService } from "@/core/services/factory";
import {
  EVENT_LIBRARY_PAGE_SIZE,
  EMPTY_EVENT_LIBRARY_DATE_FILTERS,
  parseEventLibraryCategory,
  parseEventLibraryDateFilters,
  parseEventLibraryTab,
  parseEventLibrarySort,
  parseEventPage,
  parseEventPageSize,
  parseEventSearchQuery,
  type EventAdminListItem,
} from "@/core/services/types/event.types";
import { useLibraryResultsFreeze } from "@/hooks/useLibraryResultsFreeze";
import { useLibrarySearchSnapshot } from "@/hooks/useLibrarySearchSnapshot";
import { useTranslations } from "@/lib/useTranslations";

export function EventsLibraryPage() {
  useLibrarySearchSnapshot("events");
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tab = parseEventLibraryTab(searchParams.get("status") ?? undefined);
  const category = parseEventLibraryCategory(
    searchParams.get("category") ?? undefined,
  );
  const dateFilters = parseEventLibraryDateFilters(searchParams);
  const page = parseEventPage(searchParams.get("page") ?? undefined);
  const pageSize = parseEventPageSize(searchParams.get("size") ?? undefined);
  const searchQuery = parseEventSearchQuery(searchParams.get("q") ?? undefined);
  const sort = parseEventLibrarySort(
    searchParams.get("sort") ?? undefined,
    searchParams.get("dir") ?? undefined,
  );
  const t = useTranslations("events.library");
  const queryFingerprint = location.search;

  const { data: stats } = useQuery({
    queryKey: ["events-library-stats"],
    queryFn: async () => {
      const events = createEventService();
      return events.getLibraryStats();
    },
    staleTime: 60_000,
  });

  const { data: tabCounts, isFetching: isTabCountsFetching } = useQuery({
    queryKey: [
      "events-library-tab-counts",
      category,
      dateFilters.startFrom,
      dateFilters.startTo,
      dateFilters.endFrom,
      dateFilters.endTo,
      searchQuery,
    ],
    queryFn: async () => {
      const events = createEventService();
      return events.countByLibraryTab(searchQuery, category, dateFilters);
    },
    placeholderData: keepPreviousData,
  });

  const { data: listResult, isFetching: isListFetching } = useQuery({
    queryKey: [
      "events-library-list",
      tab,
      category,
      dateFilters.startFrom,
      dateFilters.startTo,
      dateFilters.endFrom,
      dateFilters.endTo,
      page,
      pageSize,
      searchQuery,
      sort?.column,
      sort?.direction,
    ],
    queryFn: async () => {
      const events = createEventService();
      return events.listByLibraryTab(
        tab,
        page,
        pageSize,
        searchQuery,
        category,
        dateFilters,
        sort,
      );
    },
    placeholderData: keepPreviousData,
  });

  const {
    isFrozen,
    displayResult,
    toggleFreeze,
    canFreeze,
  } = useLibraryResultsFreeze<EventAdminListItem>(
    "events",
    queryFingerprint,
    listResult,
  );

  if (!stats || !displayResult || !tabCounts) {
    return (
      <div className="mx-auto flex w-full flex-1 items-center justify-center px-4 py-10 text-sm text-neutral-500 lg:px-8">
        Chargement…
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full flex-1 flex-col gap-6 overflow-y-auto px-4 py-10 lg:px-8">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <p className="text-sm font-medium uppercase tracking-widest text-neutral-500">
            {t("eyebrow")}
          </p>
          <h1 className="text-3xl font-semibold tracking-tight text-whart-text">
            {t("title")}
          </h1>
          <p className="text-sm text-neutral-600">{t("description")}</p>
        </div>
        <EventsLibraryCreateButton />
      </header>

      <EventsLibraryKpisPanel
        kpis={stats.kpis}
        sectionLabel={t("kpisNav")}
        labels={{
          total: t("kpis.total"),
          publishedThisMonth: t("kpis.publishedThisMonth"),
          pendingReview: t("kpis.pendingReview"),
        }}
      />

      <EventsLibraryPanel
        items={displayResult.items}
        total={displayResult.total}
        page={displayResult.page}
        pageSize={displayResult.pageSize}
        tab={tab}
        category={category}
        dateFilters={dateFilters}
        tabCounts={tabCounts}
        categoryCounts={stats.categoryCounts}
        searchQuery={searchQuery}
        sort={sort}
        isFrozen={isFrozen}
        onToggleResultsFreeze={toggleFreeze}
        canFreezeResults={canFreeze}
        isRefreshing={!isFrozen && (isListFetching || isTabCountsFetching)}
      />
    </div>
  );
}
